import { getSystemColorTheme } from "theme";
import type { APIAnnotationType, APIAnnotationVisibility } from "types/api_types";
import { defaultDatasetViewConfiguration } from "types/schemas/dataset_view_configuration.schema";
import constants from "viewer/constants";
import Constants, {
  ControlModeEnum,
  FillModeEnum,
  OrthoViews,
  OverwriteModeEnum,
  TDViewDisplayModeEnum,
  UnitLong,
  ViewModeValues,
} from "viewer/constants";
import { AnnotationTool, Toolkit } from "viewer/model/accessors/tool_accessor";
import type { WebknossosState } from "viewer/store";
import { getAllDefaultKeyboardShortcuts } from "viewer/view/keyboard_shortcuts/keyboard_shortcut_constants";

const defaultViewportRect = {
  top: 0,
  left: 0,
  width: Constants.VIEWPORT_WIDTH,
  height: Constants.VIEWPORT_WIDTH,
};
const initialAnnotationInfo = {
  annotationId: "",
  restrictions: {
    branchPointsAllowed: false,
    allowUpdate: false,
    allowSave: false,
    allowFinish: false,
    allowAccess: true,
    allowDownload: false,
    somaClickingAllowed: false,
    mergerMode: false,
    volumeInterpolationAllowed: false,
    allowedModes: ViewModeValues,
    magRestrictions: {},
  },
  visibility: "Internal" as APIAnnotationVisibility,
  tags: [],
  description: "",
  name: "",
  tracingStore: {
    name: "localhost",
    url: "http://localhost:9000",
  },
  annotationType: "View" as APIAnnotationType,
  meshes: [],
};

const defaultState: WebknossosState = {
  datasetConfiguration: defaultDatasetViewConfiguration,
  userConfiguration: {
    autoSaveLayouts: true,
    autoRenderMeshInProofreading: true,
    selectiveVisibilityInProofreading: true,
    isMultiSplitActive: false,
    brushSize: 50,
    clippingDistance: 50,
    clippingDistanceFlight: 64,
    clipSkeletonToCurrentSection: false,
    crosshairSize: 0.1,
    displayCrosshair: true,
    displayScalebars: true,
    dynamicSpaceDirection: false,
    hideTreeRemovalWarning: false,
    highlightCommentedNodes: false,
    keyboardDelay: 200,
    mouseRotateValue: 0.004,
    moveValue3d: 300,
    moveValue: 300,
    newNodeNewTree: false,
    continuousNodeCreation: false,
    centerNewNode: true,
    applyNodeRotationOnActivation: false,
    overrideNodeRadius: true,
    particleSize: 5,
    presetBrushSizes: null,
    rotateValue: 0.01,
    sortCommentsAsc: true,
    sortTreesByName: false,
    sphericalCapRadius: Constants.DEFAULT_SPHERICAL_CAP_RADIUS,
    tdViewDisplayPlanes: TDViewDisplayModeEnum.DATA,
    tdViewDisplayDatasetBorders: true,
    tdViewUsePerspectiveCamera: true,
    gpuMemoryFactor: Constants.DEFAULT_GPU_MEMORY_FACTOR,
    overwriteMode: OverwriteModeEnum.OVERWRITE_ALL,
    fillMode: FillModeEnum._2D,
    isFloodfillRestrictedToBoundingBox: false,
    useLegacyBindings: false,
    quickSelect: {
      useHeuristic: false,
      predictionDepth: 1,
      showPreview: false,
      segmentMode: "light",
      threshold: 128,
      closeValue: 3,
      erodeValue: 1,
      dilateValue: 2,
    },
    renderWatermark: true,
    antialiasRendering: false,
    activeToolkit: Toolkit.ALL_TOOLS,
    erasePreference: "ERASE_BRUSH",
    writePreference: "BRUSH",
    measurementPreference: "LINE_MEASUREMENT",
    mipRaymarchingSteps: 128,
    mipDepthWrite: false,
    timestampsForTools: {
      [AnnotationTool.MOVE.id]: 0,
      [AnnotationTool.BRUSH.id]: 0,
      [AnnotationTool.ERASE_BRUSH.id]: 0,
      [AnnotationTool.TRACE.id]: 0,
      [AnnotationTool.ERASE_TRACE.id]: 0,
      [AnnotationTool.SKELETON.id]: 0,
      [AnnotationTool.BOUNDING_BOX.id]: 0,
      [AnnotationTool.AREA_MEASUREMENT.id]: 0,
      [AnnotationTool.QUICK_SELECT.id]: 0,
      [AnnotationTool.FILL_CELL.id]: 0,
      [AnnotationTool.PROOFREAD.id]: 0,
      [AnnotationTool.LINE_MEASUREMENT.id]: 0,
      [AnnotationTool.VOXEL_PIPETTE.id]: 0,
    },
  },
  keyboardConfiguration: {
    shortcutsConfig: getAllDefaultKeyboardShortcuts(),
    unmodifiedLayoutMap: new Map(),
  },
  temporaryConfiguration: {
    viewMode: Constants.MODE_PLANE_TRACING,
    histogramData: {},
    flightmodeRecording: false,
    controlMode: ControlModeEnum.VIEW,
    mousePosition: null,
    hoveredSegmentId: 0n,
    hoveredUnmappedSegmentId: 0n,
    activeMappingByLayer: {},
    isMergerModeEnabled: false,
    gpuSetup: {
      smallestCommonBucketCapacity:
        Constants.GPU_FACTOR_MULTIPLIER * Constants.DEFAULT_GPU_MEMORY_FACTOR,
      initializedGpuFactor: Constants.GPU_FACTOR_MULTIPLIER,
      maximumLayerCountToRender: 32,
    },
    preferredQualityForMeshPrecomputation: 2,
    preferredQualityForMeshAdHocComputation: 2,
    lastVisibleSegmentationLayerName: null,
    layerBoundingBoxVisibilities: {},
    layerBoundingBoxColors: {},
  },
  task: null,
  dataset: {
    areLayersPreprocessed: true,
    id: "dummy-dataset-id",
    name: "Loading",
    folderId: "dummy-folder-id",
    isUnreported: false,
    created: 123,
    dataSource: {
      dataLayers: [],
      scale: { factor: [5, 5, 5], unit: UnitLong.nm },
      id: {
        name: "Loading",
        team: "",
      },
    },
    metadata: null,
    tags: [],
    isPublic: false,
    isActive: true,
    isEditable: true,
    dataStore: {
      name: "localhost",
      url: "http://localhost:9000",
      allowsUpload: true,
      jobsEnabled: false,
      jobsSupportedByAvailableWorkers: [],
    },
    owningOrganization: "",
    description: null,
    directoryName: "Loading",
    isVirtual: false,
    allowedTeams: [],
    allowedTeamsCumulative: [],
    logoUrl: null,
    lastUsedByUser: 0,
    sortingKey: 123,
    publication: null,
    usedStorageBytes: 0,
    uploaderFullName: null,
  },
  annotation: {
    ...initialAnnotationInfo,
    readOnly: {
      userBoundingBoxes: [],
      boundingBox: null,
      createdTimestamp: 0,
      type: "readonly",
      tracingId: "",
      additionalAxes: [],
    },
    volumes: [],
    mappings: [],
    skeleton: null,
    owner: null,
    isLockedByOwner: false,
    contributors: [],
    collaborationMode: "OwnerOnly",
    annotationLayers: [],
    version: 0,
    earliestAccessibleVersion: 0,
    stats: {},
    organization: "",
    isUpdatingCurrentlyAllowed: initialAnnotationInfo.restrictions.allowUpdate,
  },
  save: {
    queue: [],
    isSavingDisabled: false,
    lastSaveTimestamp: 0,
    progressInfo: {
      processedActionCount: 0,
      totalActionCount: 0,
    },
    mutexState: { hasAnnotationMutex: false, blockedByUser: null, blockedBySessionId: null },
    rebaseRelevantServerAnnotationState: {
      annotationDescription: "",
      annotationVersion: 1,
      skeleton: undefined,
      volumes: [],
      mappingDataByLayer: {},
      isRebasingOrForwarding: false,
    },
    proofreadingPostProcessingInfo: null,
  },
  flycam: {
    zoomStep: 1.3,
    currentMatrix: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
    spaceDirectionOrtho: [1, 1, 1],
    direction: [0, 0, 0],
    additionalCoordinates: [],
    // The flycam matrix has a default rotation of 180° around the z axis (see flycam_reducer.tsx resetMatrix) which is already
    // calculated out of the rotation value shown to the user and stored in this property. But as the initial matrix above
    // does not have this default rotation, the correct resulting rotation value matching the identity matrix is [0,0,180].
    rotation: [0, 0, 180],
  },
  flycamInfoCache: {
    maximumZoomForAllMags: {},
  },
  viewModeData: {
    plane: {
      activeViewport: OrthoViews.PLANE_XY,
      tdCamera: {
        near: 0,
        far: 0,
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        up: [0, 0, 0],
        position: [0, 0, 0],
      },
      inputCatcherRects: {
        PLANE_XY: defaultViewportRect,
        PLANE_YZ: defaultViewportRect,
        PLANE_XZ: defaultViewportRect,
        TDView: defaultViewportRect,
      },
    },
    flight: {
      inputCatcherRect: defaultViewportRect,
    },
  },
  activeUser: null,
  activeOrganization: null,
  uiInformation: {
    globalProgress: 0,
    activeTool: AnnotationTool.MOVE,
    activeUserBoundingBoxId: null,
    showDropzoneModal: false,
    showVersionRestore: false,
    isRestoringVersion: false,
    showDownloadModal: false,
    showAddScriptModal: false,
    showMergeAnnotationModal: false,
    showZarrPrivateLinksModal: false,
    showKeyboardShortcutConfigModal: false,
    showPythonClientModal: false,
    showDuplicateAnnotationModal: false,
    aIJobDrawerState: "invisible",
    showRenderAnimationModal: false,
    showShareModal: false,
    storedLayouts: {},
    isImportingMesh: false,
    isInAnnotationView: false,
    hasOrganizations: false,
    borderOpenStatus: {
      right: false,
      left: false,
    },
    theme: getSystemColorTheme(),
    isWkInitialized: false,
    isUiReady: false,
    quickSelectState: "inactive",
    areQuickSelectSettingsOpen: false,
    measurementToolInfo: {
      lastMeasuredPosition: null,
      isMeasuring: false,
    },
    voxelPipetteToolInfo: {
      pinnedPosition: null,
    },
    navbarHeight: constants.DEFAULT_NAVBAR_HEIGHT,
    contextInfo: {
      contextMenuPosition: null,
      clickedNodeId: null,
      meshId: null,
      meshIntersectionPosition: null,
      clickedBoundingBoxId: null,
      globalPosition: null,
      viewport: null,
      unmappedSegmentId: null,
    },
    mipBBoxSettings: {},
  },
  localSegmentationStateByLayer: {},
  localSkeletonState: {
    activeTreeId: null,
    activeGroupId: null,
    navigationList: {
      list: [],
      activeIndex: -1,
    },
    showSkeletons: true,
  },
  localAnnotationState: {
    idReservationsForBoundingBoxes: [],
  },
  operationContext: {
    activeOperations: [],
    childOperations: [],
  },
};
export default defaultState;
